/**
 * Gate 3 — Confidence Floor
 *
 * Rejects outputs whose stated confidence falls below the floor
 * for the envelope's materiality band. No output beats wrong
 * output — silence is preferable to an under-confident assertion.
 *
 * Trip routes to silence with reason='confidence_below_floor'.
 *
 * Floors (per locked-decisions.md Decision 1 materiality bands):
 *   ROUTINE     → 0.40
 *   ELEVATED    → 0.60
 *   MATERIAL    → 0.75
 *   EXISTENTIAL → 0.85
 */

export const CONFIDENCE_FLOORS = {
  ROUTINE: 0.40,
  ELEVATED: 0.60,
  MATERIAL: 0.75,
  EXISTENTIAL: 0.85,
};

/**
 * @param {import('../disciplineEnforcer.js').EnforcerEnvelope} envelope
 * @param {object} [config]
 * @param {Record<string,number>} [config.floorOverrides] - tenant override map
 * @returns {import('../disciplineEnforcer.js').GateResult}
 */
export function checkConfidenceFloor(envelope, config = {}) {
  const band = envelope.context?.materialityBand || 'ROUTINE';
  const floors = { ...CONFIDENCE_FLOORS, ...(config.floorOverrides || {}) };
  const floor = floors[band] ?? CONFIDENCE_FLOORS.ROUTINE;

  if (envelope.confidence == null) {
    return { gateName: 'confidence_floor', verdict: 'pass', reason: null, payload: { band, floor, confidence: null } };
  }

  if (envelope.confidence < floor) {
    return {
      gateName: 'confidence_floor',
      verdict: 'trip',
      reason: 'confidence_below_floor',
      payload: { band, floor, confidence: envelope.confidence },
    };
  }

  return {
    gateName: 'confidence_floor',
    verdict: 'pass',
    reason: null,
    payload: { band, floor, confidence: envelope.confidence },
  };
}
